// src/components/additional-acts/AdditionalActModal.jsx
import React, { useState } from 'react';
import { X, Download, FileText, CheckCircle } from 'lucide-react';
import { useQuery } from 'react-query';
import { contractAccessService } from '../../services';
import toast from 'react-hot-toast';
import SignedDocumentUpload from '../contracts/SignedDocumentUpload';
import UploadedDocumentsViewer from '../contracts/UploadedDocumentsViewer';

const AdditionalActModal = ({ act, token, contractInfo, onClose, onDownload }) => {
  const [activeTab, setActiveTab] = useState('info');

  // Fetch signed documents for this act
  const { data: signedDocs, refetch: refetchDocs } = useQuery(
    ['actSignedDocs', token, act.id],
    () => contractAccessService.getAdditionalActSignedDocuments(token, act.id),
    {
      enabled: !!token && !!act.id
    }
  );

  const handleUploadSuccess = (data) => {
    console.log('Upload successful for act:', data);
    toast.success('Documente încărcate cu succes!');
    refetchDocs();
    // Optionally refresh parent data
    if (onClose) {
      // Could call a parent refresh here if needed
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Act Adițional {act.number}
              </h2>
              <p className="text-sm text-gray-600">
                Contract {contractInfo.contractNumber} - {contractInfo.clientName}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('info')}
                className={`py-3 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'info'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Informații
              </button>
              <button
                onClick={() => setActiveTab('upload')}
                className={`py-3 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'upload'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Încărcare Documente
              </button>
              <button
                onClick={() => setActiveTab('documents')}
                className={`py-3 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'documents'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Documente Încărcate ({signedDocs?.totalFiles || 0})
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
            {activeTab === 'info' && (
              <div className="p-6 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold mb-3 text-gray-900">Detalii Act</h3>
                  <dl className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <dt className="text-gray-600">Număr:</dt>
                      <dd className="font-medium text-gray-900">{act.number}</dd>
                    </div>
                    <div className="flex justify-between text-sm">
                      <dt className="text-gray-600">Dată:</dt>
                      <dd className="font-medium text-gray-900">
                        {new Date(act.date).toLocaleDateString('ro-RO')}
                      </dd>
                    </div>
                    <div className="flex justify-between text-sm">
                      <dt className="text-gray-600">Status:</dt>
                      <dd>
                        {act.hasSignedDocuments ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Semnat
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            În așteptare
                          </span>
                        )}
                      </dd>
                    </div>
                  </dl>
                </div>

                <button
                  onClick={onDownload}
                  className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Descarcă PDF Act Adițional
                </button>

                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Instrucțiuni:</h4>
                  <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                    <li>Descarcă actul adițional folosind butonul de mai sus</li>
                    <li>Semnează documentul (digital sau manual)</li>
                    <li>Dacă ai semnat manual, scanează documentul în PDF</li>
                    <li>Încarcă documentul semnat în tab-ul "Încărcare Documente"</li>
                  </ol>
                </div>
              </div>
            )}

            {activeTab === 'upload' && (
              <SignedDocumentUpload
                token={token}
                uploadType="additional-act"
                entityId={act.id}
                entityLabel={`Act Adițional ${act.number}`}
                onUploadSuccess={handleUploadSuccess}
              />
            )}

            {activeTab === 'documents' && (
              <div className="p-6">
                {signedDocs?.files && signedDocs.files.length > 0 ? (
                  <div className="space-y-2">
                    {signedDocs.files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-gray-500" />
                          <span className="text-sm font-medium">{file.fileName}</span>
                        </div>
                        <span className="text-xs text-gray-500">{file.fileExtension}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Niciun document încărcat încă</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Închide
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdditionalActModal;